import { inject } from '@angular/core';
import { type ResolveFn, Routes } from '@angular/router';

import { TranslocoService, provideTranslocoScope } from '@jsverse/transloco';

import { AuthenticationFacadeService } from '@finverity/authentication';
import { CompanyType, InstrumentUploaderType } from '@finverity/graphql';
import { HasAccessGuardFactory } from '@finverity/identity-access';
import { provideMarkAsPaidConfig } from '@finverity/platform-app/modules/instrument-approval/providers';
import { INSTRUMENT_DETAILS_ROUTE_PARAM_NAME } from '@finverity/platform-app/modules/instrument-details/constants';

import { INSTRUMENT_EDIT_ROUTE_PARAM_NAME } from '../../instrument-upload/constants';
import { getFunderOrSellerInstrumentApprovalRejectedConfigs } from '../features/funder-approval-rejected-page/constants';
import {
  FUNDER_APPROVAL_REJECTED_IDENTITY_ACCESS_PROVIDER,
  FunderApprovalRejectedPermissions,
} from '../features/funder-approval-rejected-page/identity-access';
import { FUNDER_INSTRUMENT_APPROVAL_CHECKER_CONFIGS } from '../features/funder-instrument-approval-checker-page/constants';
import {
  FUNDER_INSTRUMENT_APPROVAL_CHECKER_IDENTITY_ACCESS_PROVIDER,
  FunderInstrumentApprovalCheckerPermissions,
} from '../features/funder-instrument-approval-checker-page/identity-access';
import { FUNDER_INSTRUMENT_APPROVAL_MAKER_CONFIGS } from '../features/funder-instrument-approval-maker-page/constants';
import {
  FUNDER_INSTRUMENT_APPROVAL_MAKER_IDENTITY_ACCESS_PROVIDER,
  FunderInstrumentApprovalMakerPermissions,
} from '../features/funder-instrument-approval-maker-page/identity-access';
import { MARK_INSTRUMENTS_AS_PAID_WITHOUT_FINANCING_CONFIGS } from '../features/mark-instruments-as-paid-without-financing/constants';
import {
  MARK_AS_PAID_WITHOUT_FINANCING_IDENTITY_ACCESS_PROVIDER,
  MarkAsPaidWithoutFinancingPermissions,
} from '../features/mark-instruments-as-paid-without-financing/identity-access';
import { PAYER_INSTRUMENT_APPROVAL_REJECTED_CONFIGS } from '../features/payer-approval-rejected-page/constants';
import {
  PAYER_APPROVAL_REJECTED_IDENTITY_ACCESS_PROVIDER,
  PayerApprovalRejectedPermissions,
} from '../features/payer-approval-rejected-page/identity-access';
import { PAYER_INSTRUMENT_FINANCE_REQUEST_CHECKER_CONFIGS } from '../features/payer-finance-request-checker-page/constants';
import {
  PAYER_FINANCE_REQUEST_CHECKER_IDENTITY_ACCESS_PROVIDER,
  PayerFinanceRequestCheckerPermissions,
} from '../features/payer-finance-request-checker-page/identity-access';
import { PAYER_INSTRUMENT_FINANCE_REQUEST_MAKER_CONFIGS } from '../features/payer-finance-request-maker-page/constants';
import {
  PAYER_FINANCE_REQUEST_MAKER_IDENTITY_ACCESS_PROVIDER,
  PayerFinanceRequestMakerPermissions,
} from '../features/payer-finance-request-maker-page/identity-access';
import { PAYER_INSTRUMENT_FINANCE_REQUEST_REJECTED_CONFIGS } from '../features/payer-finance-request-rejected-page/constants';
import {
  PAYER_FINANCE_REQUEST_REJECTED_IDENTITY_ACCESS_PROVIDER,
  PayerFinanceRequestRejectedPermissions,
} from '../features/payer-finance-request-rejected-page/identity-access';
import { PAYER_INSTRUMENT_APPROVAL_CHECKER_CONFIGS } from '../features/payer-instrument-approval-checker-page/constants';
import {
  PAYER_INSTRUMENT_APPROVAL_CHECKER_IDENTITY_ACCESS_PROVIDER,
  PayerInstrumentApprovalCheckerPermissions,
} from '../features/payer-instrument-approval-checker-page/identity-access';
import { PAYER_INSTRUMENT_APPROVAL_MAKER_CONFIGS } from '../features/payer-instrument-approval-maker-page/constants';
import {
  PAYER_INSTRUMENT_APPROVAL_MAKER_IDENTITY_ACCESS_PROVIDER,
  PayerInstrumentApprovalMakerPermissions,
} from '../features/payer-instrument-approval-maker-page/identity-access';
import { SELLER_INSTRUMENT_FINANCE_REQUEST_CHECKER_CONFIGS } from '../features/seller-finance-request-checker-page/constants';
import {
  SELLER_FINANCE_REQUEST_CHECKER_IDENTITY_ACCESS_PROVIDER,
  SellerFinanceRequestCheckerPermissions,
} from '../features/seller-finance-request-checker-page/identity-access';
import { SELLER_INSTRUMENT_FINANCE_REQUEST_MAKER_CONFIGS } from '../features/seller-finance-request-maker-page/constants';
import {
  SELLER_FINANCE_REQUEST_MAKER_IDENTITY_ACCESS_PROVIDER,
  SellerFinanceRequestMakerPermissions,
} from '../features/seller-finance-request-maker-page/identity-access';
import { SELLER_INSTRUMENT_FINANCE_REQUEST_REJECTED_CONFIGS } from '../features/seller-finance-request-rejected-page/constants';
import {
  SELLER_FINANCE_REQUEST_REJECTED_IDENTITY_ACCESS_PROVIDER,
  SellerFinanceRequestRejectedPermissions,
} from '../features/seller-finance-request-rejected-page/identity-access';
import { provideInstrumentApprovalConfigs } from '../providers';

const browserTitleResolver =
  (key: string): ResolveFn<string> =>
  () =>
    inject(TranslocoService).translate(key);

const INSTRUMENT_APPROVAL_SCOPE_PROVIDER = provideTranslocoScope({ scope: 'instrument-approval' });

const INSTRUMENT_APPROVAL_ROUTES: Routes = [
  {
    path: 'payer-approval-maker',
    title: browserTitleResolver('instrument-approval.routes.browserTitle.payerApprovalMaker'),
    providers: [
      provideInstrumentApprovalConfigs(PAYER_INSTRUMENT_APPROVAL_MAKER_CONFIGS),
      PAYER_INSTRUMENT_APPROVAL_MAKER_IDENTITY_ACCESS_PROVIDER,
      INSTRUMENT_APPROVAL_SCOPE_PROVIDER,
    ],
    loadComponent: () =>
      import('../features/payer-instrument-approval-maker-page/payer-instrument-approval-maker-page.component'),
    canActivate: [
      HasAccessGuardFactory([[PayerInstrumentApprovalMakerPermissions.ViewInstruments]], [CompanyType.Customer]),
    ],
    children: [
      {
        path: `view/:${INSTRUMENT_DETAILS_ROUTE_PARAM_NAME}`,
        loadChildren: () => import('../../instrument-details/shell/instrument-details.routes'),
        canActivate: [
          HasAccessGuardFactory(
            [[PayerInstrumentApprovalMakerPermissions.ViewInstrumentDetails]],
            [CompanyType.Customer]
          ),
        ],
      },
    ],
  },
  {
    path: 'payer-approval-checker',
    title: browserTitleResolver('instrument-approval.routes.browserTitle.payerApprovalChecker'),
    providers: [
      provideInstrumentApprovalConfigs(PAYER_INSTRUMENT_APPROVAL_CHECKER_CONFIGS),
      PAYER_INSTRUMENT_APPROVAL_CHECKER_IDENTITY_ACCESS_PROVIDER,
      INSTRUMENT_APPROVAL_SCOPE_PROVIDER,
    ],
    loadComponent: () =>
      import('../features/payer-instrument-approval-checker-page/payer-instrument-approval-checker-page.component'),
    canActivate: [
      HasAccessGuardFactory([[PayerInstrumentApprovalCheckerPermissions.ViewInstruments]], [CompanyType.Customer]),
    ],
    children: [
      {
        path: `view/:${INSTRUMENT_DETAILS_ROUTE_PARAM_NAME}`,
        loadChildren: () => import('../../instrument-details/shell/instrument-details.routes'),
        canActivate: [
          HasAccessGuardFactory(
            [[PayerInstrumentApprovalCheckerPermissions.ViewInstrumentDetails]],
            [CompanyType.Customer]
          ),
        ],
      },
    ],
  },
  {
    path: 'payer-approval-rejected',
    title: browserTitleResolver('instrument-approval.routes.browserTitle.payerApprovalRejected'),
    providers: [
      provideInstrumentApprovalConfigs(PAYER_INSTRUMENT_APPROVAL_REJECTED_CONFIGS),
      PAYER_APPROVAL_REJECTED_IDENTITY_ACCESS_PROVIDER,
      INSTRUMENT_APPROVAL_SCOPE_PROVIDER,
    ],
    loadComponent: () => import('../features/payer-approval-rejected-page/payer-approval-rejected-page.component'),
    canActivate: [
      HasAccessGuardFactory(
        [[PayerApprovalRejectedPermissions.ViewInstruments, PayerApprovalRejectedPermissions.EditInstrument]],
        [CompanyType.Customer]
      ),
    ],
    children: [
      {
        path: `view/:${INSTRUMENT_DETAILS_ROUTE_PARAM_NAME}`,
        loadChildren: () => import('../../instrument-details/shell/instrument-details.routes'),
        canActivate: [
          HasAccessGuardFactory([[PayerApprovalRejectedPermissions.ViewInstrumentDetails]], [CompanyType.Customer]),
        ],
      },
      {
        path: `edit/:${INSTRUMENT_EDIT_ROUTE_PARAM_NAME}`,
        loadChildren: () => import('../../instrument-upload/features/instrument-edit/shell/instrument-edit.routes'),
        canActivate: [
          HasAccessGuardFactory([[PayerApprovalRejectedPermissions.EditInstrument]], [CompanyType.Customer]),
        ],
      },
    ],
  },
  {
    path: 'mark-as-paid-without-financing',
    title: browserTitleResolver('instrument-approval.routes.browserTitle.markAsPaidWithoutFinancing'),
    providers: [
      provideMarkAsPaidConfig(MARK_INSTRUMENTS_AS_PAID_WITHOUT_FINANCING_CONFIGS),
      MARK_AS_PAID_WITHOUT_FINANCING_IDENTITY_ACCESS_PROVIDER,
      INSTRUMENT_APPROVAL_SCOPE_PROVIDER,
    ],
    loadComponent: () =>
      import('../features/mark-instruments-as-paid-without-financing/mark-instruments-without-financing-page.component'),
    canActivate: [
      HasAccessGuardFactory([[MarkAsPaidWithoutFinancingPermissions.ViewInstruments]], [CompanyType.Customer]),
    ],
    children: [
      {
        path: `view/:${INSTRUMENT_DETAILS_ROUTE_PARAM_NAME}`,
        loadChildren: () => import('../../instrument-details/shell/instrument-details.routes'),
        canActivate: [
          HasAccessGuardFactory(
            [[MarkAsPaidWithoutFinancingPermissions.ViewInstrumentDetails]],
            [CompanyType.Customer]
          ),
        ],
      },
    ],
  },
  {
    path: 'seller-finance-request-maker',
    title: browserTitleResolver('instrument-approval.routes.browserTitle.sellerFinanceRequestMaker'),
    providers: [
      provideInstrumentApprovalConfigs(SELLER_INSTRUMENT_FINANCE_REQUEST_MAKER_CONFIGS),
      SELLER_FINANCE_REQUEST_MAKER_IDENTITY_ACCESS_PROVIDER,
      INSTRUMENT_APPROVAL_SCOPE_PROVIDER,
    ],
    loadComponent: () =>
      import('../features/seller-finance-request-maker-page/seller-finance-request-maker-page.component'),
    canActivate: [
      HasAccessGuardFactory([[SellerFinanceRequestMakerPermissions.ViewInstruments]], [CompanyType.Customer]),
    ],
    children: [
      {
        path: `view/:${INSTRUMENT_DETAILS_ROUTE_PARAM_NAME}`,
        loadChildren: () => import('../../instrument-details/shell/instrument-details.routes'),
        canActivate: [
          HasAccessGuardFactory([[SellerFinanceRequestMakerPermissions.ViewInstrumentDetails]], [CompanyType.Customer]),
        ],
      },
    ],
  },
  {
    path: 'seller-finance-request-checker',
    title: browserTitleResolver('instrument-approval.routes.browserTitle.sellerFinanceRequestChecker'),
    providers: [
      provideInstrumentApprovalConfigs(SELLER_INSTRUMENT_FINANCE_REQUEST_CHECKER_CONFIGS),
      SELLER_FINANCE_REQUEST_CHECKER_IDENTITY_ACCESS_PROVIDER,
      INSTRUMENT_APPROVAL_SCOPE_PROVIDER,
    ],
    loadComponent: () =>
      import('../features/seller-finance-request-checker-page/seller-finance-request-checker-page.component'),
    canActivate: [
      HasAccessGuardFactory([[SellerFinanceRequestCheckerPermissions.ViewInstruments]], [CompanyType.Customer]),
    ],
    children: [
      {
        path: `view/:${INSTRUMENT_DETAILS_ROUTE_PARAM_NAME}`,
        loadChildren: () => import('../../instrument-details/shell/instrument-details.routes'),
        canActivate: [
          HasAccessGuardFactory(
            [[SellerFinanceRequestCheckerPermissions.ViewInstrumentDetails]],
            [CompanyType.Customer]
          ),
        ],
      },
    ],
  },
  {
    path: 'seller-finance-request-rejected',
    title: browserTitleResolver('instrument-approval.routes.browserTitle.sellerFinanceRequestRejected'),
    providers: [
      provideInstrumentApprovalConfigs(SELLER_INSTRUMENT_FINANCE_REQUEST_REJECTED_CONFIGS),
      SELLER_FINANCE_REQUEST_REJECTED_IDENTITY_ACCESS_PROVIDER,
      INSTRUMENT_APPROVAL_SCOPE_PROVIDER,
    ],
    loadComponent: () =>
      import('../features/seller-finance-request-rejected-page/seller-finance-request-rejected-page.component'),
    canActivate: [
      HasAccessGuardFactory(
        [
          [
            SellerFinanceRequestRejectedPermissions.ViewInstruments,
            SellerFinanceRequestRejectedPermissions.EditInstrument,
          ],
        ],
        [CompanyType.Customer]
      ),
    ],
    children: [
      {
        path: `view/:${INSTRUMENT_DETAILS_ROUTE_PARAM_NAME}`,
        loadChildren: () => import('../../instrument-details/shell/instrument-details.routes'),
        canActivate: [
          HasAccessGuardFactory(
            [[SellerFinanceRequestRejectedPermissions.ViewInstrumentDetails]],
            [CompanyType.Customer]
          ),
        ],
      },
      {
        path: `edit/:${INSTRUMENT_EDIT_ROUTE_PARAM_NAME}`,
        loadChildren: () => import('../../instrument-upload/features/instrument-edit/shell/instrument-edit.routes'),
        canActivate: [
          HasAccessGuardFactory([[SellerFinanceRequestRejectedPermissions.EditInstrument]], [CompanyType.Customer]),
        ],
      },
    ],
  },
  {
    path: 'payer-finance-request-maker',
    title: browserTitleResolver('instrument-approval.routes.browserTitle.payerFinanceRequestMaker'),
    providers: [
      provideInstrumentApprovalConfigs(PAYER_INSTRUMENT_FINANCE_REQUEST_MAKER_CONFIGS),
      PAYER_FINANCE_REQUEST_MAKER_IDENTITY_ACCESS_PROVIDER,
      INSTRUMENT_APPROVAL_SCOPE_PROVIDER,
    ],
    loadComponent: () =>
      import('../features/payer-finance-request-maker-page/payer-finance-request-maker-page.component'),
    canActivate: [
      HasAccessGuardFactory([[PayerFinanceRequestMakerPermissions.ViewInstruments]], [CompanyType.Customer]),
    ],
    children: [
      {
        path: `view/:${INSTRUMENT_DETAILS_ROUTE_PARAM_NAME}`,
        loadChildren: () => import('../../instrument-details/shell/instrument-details.routes'),
        canActivate: [
          HasAccessGuardFactory([[PayerFinanceRequestMakerPermissions.ViewInstrumentDetails]], [CompanyType.Customer]),
        ],
      },
    ],
  },
  {
    path: 'payer-finance-request-checker',
    title: browserTitleResolver('instrument-approval.routes.browserTitle.payerFinanceRequestChecker'),
    providers: [
      provideInstrumentApprovalConfigs(PAYER_INSTRUMENT_FINANCE_REQUEST_CHECKER_CONFIGS),
      PAYER_FINANCE_REQUEST_CHECKER_IDENTITY_ACCESS_PROVIDER,
      INSTRUMENT_APPROVAL_SCOPE_PROVIDER,
    ],
    loadComponent: () =>
      import('../features/payer-finance-request-checker-page/payer-finance-request-checker-page.component'),
    canActivate: [
      HasAccessGuardFactory([[PayerFinanceRequestCheckerPermissions.ViewInstruments]], [CompanyType.Customer]),
    ],
    children: [
      {
        path: `view/:${INSTRUMENT_DETAILS_ROUTE_PARAM_NAME}`,
        loadChildren: () => import('../../instrument-details/shell/instrument-details.routes'),
        canActivate: [
          HasAccessGuardFactory(
            [[PayerFinanceRequestCheckerPermissions.ViewInstrumentDetails]],
            [CompanyType.Customer]
          ),
        ],
      },
    ],
  },
  {
    path: 'payer-finance-request-rejected',
    title: browserTitleResolver('instrument-approval.routes.browserTitle.payerFinanceRequestRejected'),
    providers: [
      provideInstrumentApprovalConfigs(PAYER_INSTRUMENT_FINANCE_REQUEST_REJECTED_CONFIGS),
      PAYER_FINANCE_REQUEST_REJECTED_IDENTITY_ACCESS_PROVIDER,
      INSTRUMENT_APPROVAL_SCOPE_PROVIDER,
    ],
    loadComponent: () =>
      import('../features/payer-finance-request-rejected-page/payer-finance-request-rejected-page.component'),
    canActivate: [
      HasAccessGuardFactory(
        [
          [
            PayerFinanceRequestRejectedPermissions.ViewInstruments,
            PayerFinanceRequestRejectedPermissions.EditInstrument,
          ],
        ],
        [CompanyType.Customer]
      ),
    ],
    children: [
      {
        path: `view/:${INSTRUMENT_DETAILS_ROUTE_PARAM_NAME}`,
        loadChildren: () => import('../../instrument-details/shell/instrument-details.routes'),
        canActivate: [
          HasAccessGuardFactory(
            [[PayerFinanceRequestRejectedPermissions.ViewInstrumentDetails]],
            [CompanyType.Customer]
          ),
        ],
      },
      {
        path: `edit/:${INSTRUMENT_EDIT_ROUTE_PARAM_NAME}`,
        loadChildren: () => import('../../instrument-upload/features/instrument-edit/shell/instrument-edit.routes'),
        canActivate: [
          HasAccessGuardFactory([[PayerFinanceRequestRejectedPermissions.EditInstrument]], [CompanyType.Customer]),
        ],
      },
    ],
  },
  {
    path: 'funder-approval-maker',
    title: browserTitleResolver('instrument-approval.routes.browserTitle.funderApprovalMaker'),
    providers: [
      provideInstrumentApprovalConfigs(FUNDER_INSTRUMENT_APPROVAL_MAKER_CONFIGS),
      FUNDER_INSTRUMENT_APPROVAL_MAKER_IDENTITY_ACCESS_PROVIDER,
      INSTRUMENT_APPROVAL_SCOPE_PROVIDER,
    ],
    loadComponent: () =>
      import('../features/funder-instrument-approval-maker-page/funder-instrument-approval-maker-page.component'),
    canActivate: [
      HasAccessGuardFactory(
        [[FunderInstrumentApprovalMakerPermissions.ViewInstruments]],
        [CompanyType.Funder, CompanyType.PortalAdmin]
      ),
    ],
    children: [
      {
        path: `view/:${INSTRUMENT_DETAILS_ROUTE_PARAM_NAME}`,
        loadChildren: () => import('../../instrument-details/shell/instrument-details.routes'),
        canActivate: [
          HasAccessGuardFactory(
            [[FunderInstrumentApprovalMakerPermissions.ViewInstrumentDetails]],
            [CompanyType.Funder, CompanyType.PortalAdmin]
          ),
        ],
      },
    ],
  },
  {
    path: 'funder-approval-checker',
    title: browserTitleResolver('instrument-approval.routes.browserTitle.funderApprovalChecker'),
    providers: [
      provideInstrumentApprovalConfigs(FUNDER_INSTRUMENT_APPROVAL_CHECKER_CONFIGS),
      FUNDER_INSTRUMENT_APPROVAL_CHECKER_IDENTITY_ACCESS_PROVIDER,
      INSTRUMENT_APPROVAL_SCOPE_PROVIDER,
    ],
    loadComponent: () =>
      import('../features/funder-instrument-approval-checker-page/funder-instrument-approval-checker-page.component'),
    canActivate: [
      HasAccessGuardFactory(
        [[FunderInstrumentApprovalCheckerPermissions.ViewInstruments]],
        [CompanyType.Funder, CompanyType.PortalAdmin]
      ),
    ],
    children: [
      {
        path: `view/:${INSTRUMENT_DETAILS_ROUTE_PARAM_NAME}`,
        loadChildren: () => import('../../instrument-details/shell/instrument-details.routes'),
        canActivate: [
          HasAccessGuardFactory(
            [[FunderInstrumentApprovalCheckerPermissions.ViewInstrumentDetails]],
            [CompanyType.Funder, CompanyType.PortalAdmin]
          ),
        ],
      },
    ],
  },
  createFunderApprovalRejectedRoute(InstrumentUploaderType.DealManagerFunder),
  createFunderApprovalRejectedRoute(InstrumentUploaderType.Seller),
];

export default INSTRUMENT_APPROVAL_ROUTES;

function createFunderApprovalRejectedRoute(
  instrumentUploaderType: InstrumentUploaderType.DealManagerFunder | InstrumentUploaderType.Seller
) {
  const canMatchArgumentsMapping = {
    [InstrumentUploaderType.DealManagerFunder]: CompanyType.Funder,
    [InstrumentUploaderType.Seller]: CompanyType.Customer,
  };

  const titleKey =
    canMatchArgumentsMapping[instrumentUploaderType] === CompanyType.Funder
      ? 'instrument-approval.routes.browserTitle.funderApprovalRejectedAsFunder'
      : 'instrument-approval.routes.browserTitle.funderApprovalRejectedAsSeller';

  return {
    path: 'funder-approval-rejected',
    title: browserTitleResolver(titleKey),
    providers: [
      provideInstrumentApprovalConfigs(getFunderOrSellerInstrumentApprovalRejectedConfigs(instrumentUploaderType)),
      FUNDER_APPROVAL_REJECTED_IDENTITY_ACCESS_PROVIDER,
      INSTRUMENT_APPROVAL_SCOPE_PROVIDER,
    ],
    loadComponent: () => import('../features/funder-approval-rejected-page/funder-approval-rejected-page.component'),
    canActivate: [
      HasAccessGuardFactory([
        [FunderApprovalRejectedPermissions.ViewInstruments, FunderApprovalRejectedPermissions.EditInstrument],
      ]),
    ],
    canMatch: [
      () =>
        inject(AuthenticationFacadeService).userSignal()?.company_type ===
          canMatchArgumentsMapping[instrumentUploaderType] ||
        inject(AuthenticationFacadeService).userSignal()?.company_type === CompanyType.PortalAdmin,
    ],
    children: [
      {
        path: `view/:${INSTRUMENT_DETAILS_ROUTE_PARAM_NAME}`,
        loadChildren: () => import('../../instrument-details/shell/instrument-details.routes'),
        canActivate: [HasAccessGuardFactory([[FunderApprovalRejectedPermissions.ViewInstrumentDetails]])],
      },
      {
        path: `edit/:${INSTRUMENT_EDIT_ROUTE_PARAM_NAME}`,
        loadChildren: () => import('../../instrument-upload/features/instrument-edit/shell/instrument-edit.routes'),
        canActivate: [HasAccessGuardFactory([[FunderApprovalRejectedPermissions.EditInstrument]])],
      },
    ],
  };
}
